import { createAsyncThunk, createSlice } from "@reduxjs/toolkit"

import { handleAuthentication, showSnackBar, getLabsCart } from "@containers/App/reducer"

import { APIEndpoints, DEFAULT_ERROR_MESSAGE, SNACK_BAR_DELAY_TIME } from "../constants"
import { trackInventoryPageInfo } from "./tracking"
import { parseLayoutData } from "./parser"

const initialState = {
    layout: [],
    data: {},
    isFetched: false,
    isFetching: false,
    isFetchError: false,
    error: null,
    isFetchingIntermediate: false,
    /**
     * using pageMetaData as a protected object which is to be used when
     * we need to save any meta data related to this page
     */
    pageMetaData: {
        loggedIn: false,
    },
}

export const fetchInventoryData = createAsyncThunk(
    "inventoryData/fetchInventoryData",
    async (_arg, { getState, extra, rejectWithValue, dispatch }) => {
        const params = _arg

        const { loggedIn } = getState().shellReducer
        dispatch(setPageMetaDataLoggedIn(loggedIn))

        const url = APIEndpoints.INVENTORIES.uri()

        const { api } = extra

        try {
            const response = await api.get(url, { params })
            if (response?.is_success) {
                trackInventoryPageInfo(response?.data?.ga_data)
                return Promise.resolve(response?.data)
            }
        } catch (err) {
            const onServer = typeof window === "undefined"
            if (!onServer && (err.status_code === 403 || err.status_code === 401))
                dispatch(handleAuthentication())
            return rejectWithValue(err)
        }
    }
)

export const attachLab = createAsyncThunk(
    "inventoryData/attachLab",
    async (_arg, { extra, rejectWithValue, dispatch }) => {
        const { params, onReplace } = _arg

        const url = APIEndpoints.PUT_LAB.uri()

        const { api } = extra

        try {
            const response = await api.put(url, params)
            if (response?.is_success) {
                dispatch(getLabsCart())
                onReplace?.()

                return Promise.resolve(response?.data)
            }
        } catch (err) {
            showSnackBar(dispatch, DEFAULT_ERROR_MESSAGE, SNACK_BAR_DELAY_TIME)
            return rejectWithValue(err)
        }
    }
)

export const inventorySlice = createSlice({
    name: "inventoryData",
    initialState: initialState,
    reducers: {
        reset: () => initialState,
        fetchIntermediateFiltersSuccess: (state, action) => {
            state.intermediateFilterData = {
                ...state.filters,
                values: action.payload.data?.filters,
            }
            state.intermediateFiltersFetching = false
            state.intermediateFiltersFetched = true
        },
        setPageMetaDataLoggedIn: (state, action) => {
            state.pageMetaData.loggedIn = action.payload
        },
        setIsFetchingIntermediate: (state, action) => {
            state.isFetchingIntermediate = action.payload
        },
        customInventoryError: (state, action) => {
            state.error = action.payload
            state.isFetchError = true
        },
    },
    extraReducers: (builder) => {
        builder.addCase(fetchInventoryData.pending, (state) => {
            state.isFetching = true
        }),
            builder.addCase(fetchInventoryData.fulfilled, (state, action) => {
                state.layout = parseLayoutData(action.payload?.rows)
                state.isFetching = false
                state.isFetchingIntermediate = false
                state.isFetched = true
                state.isFetchError = false
                state.error = null
            }),
            builder.addCase(fetchInventoryData.rejected, (state, action) => {
                state.isFetching = false
                state.isFetchingIntermediate = false
                state.error = action.payload
                state.isFetchError = true
                state.layout = []
            })
    },
})

export const inventoryReducer = inventorySlice.reducer
export const { reset, setTest, setPageMetaDataLoggedIn, setIsFetchingIntermediate, customInventoryError } =
    inventorySlice.actions
